import React from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import useScrollAnimation from './useScrollAnimation';

const comparisonModels = [
  {
    id: 'i17-air',
    name: 'Peace i17 Air',
    tagline: 'The ultimate titanium flagship.',
    price: 'From $999',
    image: '/Photo/i7 air 2.jpg',
    isHero: true,
    specs: {
      screen: '6.7″ or 6.1″ Super Retina XDR with ProMotion',
      chip: 'A17 Pro chip with 6-core GPU',
      camera: 'Pro camera system (48MP Main | Ultra Wide | 5x Telephoto)',
      battery: 'Up to 29 hours video playback',
      material: 'Titanium with textured matte glass back',
      connector: 'USB-C with USB 3 speeds (up to 10Gb/s)'
    }
  },
  {
    id: 'nova-17-pro',
    name: 'Peace Nova 17 Pro',
    tagline: 'Powerful pro capabilities.',
    price: 'From $899',
    image: '/Photo/nova 17 pro 1.jpg',
    isHero: false,
    specs: {
      screen: '6.1″ Super Retina XDR display',
      chip: 'A16 Bionic chip with 5-core GPU',
      camera: 'Advanced pro camera system (48MP Main | Ultra Wide | 3x Telephoto)',
      battery: 'Up to 23 hours video playback',
      material: 'Aerospace-grade aluminum with glass back',
      connector: 'USB-C connector'
    }
  },
  {
    id: 'sc26',
    name: 'Peace SC26',
    tagline: 'Remarkable all-around power.',
    price: 'From $699',
    image: '/Photo/SC26 front.jpg',
    isHero: false,
    specs: {
      screen: '6.7″ Super Retina OLED display',
      chip: 'Octa-core AI Powerhouse',
      camera: 'Advanced dual-camera system (50MP Main | Ultra Wide)',
      battery: 'Up to 26 hours video playback',
      material: 'Durable composite frame with Ceramic shield',
      connector: 'Fast charge USB-C'
    }
  },
  {
    id: 'mini-5',
    name: 'Peace Mini',
    tagline: 'Massive power in mini size.',
    price: 'From $499',
    image: '/Photo/mini 1 front.jpg',
    isHero: false,
    specs: {
      screen: '5.4″ Compact Super Retina display',
      chip: 'A15 Fast Bionic chip',
      camera: 'Dual-camera system (12MP Main | Ultra Wide)',
      battery: 'Up to 19 hours video playback',
      material: 'Anodized aluminum edge design',
      connector: 'USB-C connector'
    }
  }
];

const ComparisonSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section 
      ref={sectionRef} 
      className={`comparison-section ${isVisible ? 'is-visible' : ''}`}
      id="compare"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center comparison-header scroll-fade-up">
          <span className="section-eyebrow">Lineup Comparison</span>
          <h2 className="section-headline">Which model is right for you?</h2>
          <p className="section-subheadline">
            Compare key features, display sizes, performance, and camera capabilities across the Peace smartphone lineup.
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="comparison-cards-grid">
          {comparisonModels.map((model) => (
            <div 
              key={model.id} 
              className={`compare-card scroll-fade-up ${model.isHero ? 'compare-card-highlight' : ''}`}
            >
              {model.isHero && <div className="highlight-ribbon">Most Advanced</div>}

              <div className="compare-img-box">
                <img 
                  src={model.image} 
                  alt={model.name} 
                  className="compare-product-img"
                />
              </div>

              <div className="compare-model-info">
                <h3 className="compare-name">{model.name}</h3>
                <p className="compare-tagline">{model.tagline}</p>
                <div className="compare-price">{model.price}</div>

                <div className="compare-actions">
                  <Link to="/products" className="apple-btn apple-btn-primary full-width">
                    Buy {model.name}
                  </Link>
                  <Link to="/products" className="compare-details-link">
                    Explore specs <FaChevronRight className="mini-arrow" />
                  </Link>
                </div>
              </div>

              <hr className="compare-divider" />

              {/* Specs Breakdown */}
              <div className="compare-specs-list">
                <div className="compare-spec-item">
                  <div className="spec-label">Display</div>
                  <div className="spec-value">{model.specs.screen}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Chip</div>
                  <div className="spec-value">{model.specs.chip}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Camera</div>
                  <div className="spec-value">{model.specs.camera}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Battery</div>
                  <div className="spec-value">{model.specs.battery}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Materials</div>
                  <div className="spec-value">{model.specs.material}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Connectivity</div>
                  <div className="spec-value">{model.specs.connector}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
